import { FormEvent, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  DropdownButton,
  Form,
  Navbar,
  ProgressBar,
  Row,
  Table,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { RegistrationState, Step } from "../../types/register/registerState";
import { MapPoint, TerritoryAddress } from "../../types/scheduling/territory";
import { MapWithGeoJson } from "../congregation/territory/maps";
import { CongSettings } from "../../types/scheduling/settings";
import { Position } from "geojson";
import { DefaultSrid, getCurrentLocation, mapPointFromPosition } from "../../helpers/geo";
import { smpwAPI } from "../../api/smpw";
import { localizedCountryNames, localizedLanguageNames } from "../../helpers/dateHelpers";
import { LanguagePicker } from "../languagePicker";
import HourglassGlobals from "../../helpers/globals";
import HourglassLogin from "../login";
import { t } from "i18next";
import { AddCongModal } from "../scheduling/wm/wm_cong_speakers";
import { UserInfo } from "./userInfo";
import { getModelFromState } from "./registrationStep";
import { ShowStatus, Status } from "../scheduling/common";
import { sleep } from "../../helpers/util";
import { RegisterUser } from "../../types/register/registerUser";
import { isAppleICloudDomain } from "../../helpers/email";

// SMPWPreRegistration: handles user login/signup
export function SMPWPreRegistration(props: { setHaveToken: (set: boolean) => void }) {
  return (
    <>
      <Alert variant="info">{t("smpw.pre-reg-text")}</Alert>
      <HourglassLogin forSmpwReg setHaveToken={props.setHaveToken} />
    </>
  );
}

export function SMPWRegistration() {
  const { t } = useTranslation();

  return (
    <Container fluid="md">
      <Navbar>
        <Navbar.Brand>
          {t("smpw.smpw")} — {t("register.heading")}
        </Navbar.Brand>
      </Navbar>
      <ZoneRegistrationSteps />
    </Container>
  );
}

function ZoneRegistrationSteps() {
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState(Step.Init);
  const [validated, setValidated] = useState(false);
  const [geoLocationError, setGeoLocationError] = useState(false);
  const [language, setLanguage] = useState(1);
  const [name, setName] = useState("");
  const [timezoneId, setTimezoneId] = useState(36);
  const [countryId, setCountryId] = useState(1);
  const [geoLocation, setGeoLocation] = useState<MapPoint>();
  const [status, setStatus] = useState(Status.Null);

  useEffect(() => {
    if (currentStep === Step.Info && !geoLocation) {
      getCurrentLocation().then((c) => {
        setGeoLocation({ x: c.lng, y: c.lat, srid: DefaultSrid });
        getGeoInfo({ x: c.lng, y: c.lat, srid: DefaultSrid });
      });
    }
  }, [currentStep, geoLocation]);

  const getGeoInfo = async (pos: MapPoint) => {
    try {
      const geo = await smpwAPI.getGeoInfo(pos.y, pos.x);
      setName(`${geo.city}, ${geo.state} (${geo.country})`);
      setTimezoneId(geo.time_zone_id);
      setCountryId(geo.country_id);
    } catch (e) {
      console.error(e);
    }
  };

  const selectedLanguage = (_code: string, localeId: number) => {
    setLanguage(localeId);
  };

  const handleMapClick = async (p: Position) => {
    const pos = mapPointFromPosition(p);
    setGeoLocation(pos);
    await getGeoInfo(pos);
  };

  const handleStepOne = () => {
    setCurrentStep(Step.Info);
  };

  const handleStepTwo = (e: FormEvent<HTMLFormElement>) => {
    const form = e.currentTarget;
    setValidated(true);

    // Check if geoLocation has been set
    if (!geoLocation || !geoLocation.x || !geoLocation.y) {
      setGeoLocationError(true);
      e.preventDefault();
      e.stopPropagation();
      return;
    } else {
      setGeoLocationError(false);
    }

    e.preventDefault();
    if (form.checkValidity() === false) {
      e.stopPropagation();
      return;
    }

    setCurrentStep(Step.Finish);
  };

  const handleBack = () => {
    switch (currentStep) {
      case Step.Info:
        setCurrentStep(Step.Init);
        break;
      case Step.Finish:
        setCurrentStep(Step.Info);
        break;

      default:
        setCurrentStep(Step.Init);
    }
  };

  const handleSubmit = async () => {
    setStatus(Status.Pending);
    try {
      if (!geoLocation) throw "";
      await smpwAPI.regZone({
        name: name,
        country_id: countryId,
        time_zone_id: timezoneId,
        admin_id: HourglassGlobals.authUser.id,
        geo_location: geoLocation,
      });
      setStatus(Status.Success);
      await sleep(1000);
      window.location.href = "/v2/page/app/smpw/admin";
    } catch (error) {
      setStatus(Status.Failure);
      console.error(error);
    }
  };

  switch (currentStep) {
    case Step.Init:
      return (
        <Card className="mx-auto" style={{ maxWidth: "40rem" }}>
          <Card.Header>
            {t("smpw.registration")}
            <DropdownButton
              className="float-end"
              size="sm"
              variant="secondary"
              title={
                localizedLanguageNames(i18n.language).find((l) => l.id === language)?.localized ??
                t("congprofile.language")
              }
            >
              <LanguagePicker onChange={selectedLanguage} />
            </DropdownButton>
          </Card.Header>
          <Card.Body>
            <p>{t("smpw.registration-init-text")}</p>
          </Card.Body>
          <Card.Footer>
            <Button className="float-end" onClick={handleStepOne}>
              {t("tour.button.next")}
            </Button>
          </Card.Footer>
        </Card>
      );

    case Step.Info:
      return (
        <Form noValidate validated={validated} onSubmit={handleStepTwo}>
          <Card className="mx-auto" style={{ maxWidth: "40rem" }}>
            <Card.Header>{t("smpw.registration")}</Card.Header>
            <Card.Body>
              <Card.Text>{t("smpw.registration-info-text")}</Card.Text>

              <Form.Group as={Row} className="mb-3" controlId="location">
                <Form.Label column sm="4">
                  {t("general.location")} <span className="text-danger">*</span>
                </Form.Label>
                <Col>
                  <Card className={`p-2 ${geoLocationError ? "border-danger" : ""}`}>
                    <MapWithGeoJson
                      key="rootZoneMap"
                      settings={{ terr_tile_provider: 2 } as CongSettings}
                      addresses={[{ id: 1, location: geoLocation } as TerritoryAddress]}
                      navigateToMap={() => {}}
                      setMaplibreMap={() => {}}
                      onClick={handleMapClick}
                      pubMode
                    />
                  </Card>
                  {geoLocationError && (
                    <Form.Text className="text-danger">
                      {t("general.required")}. {t("smpw.select-location")}
                    </Form.Text>
                  )}
                </Col>
              </Form.Group>

              <Card.Text>{t("smpw.city-name-metropolitan-area")}</Card.Text>

              <Form.Group as={Row} className="mb-3" controlId="name">
                <Form.Label column sm="4">
                  {t("smpw.city-name")} <span className="text-danger">*</span>
                </Form.Label>
                <Col sm="8">
                  <Form.Control required autoFocus value={name} onChange={(e) => setName(e.target.value)} />
                  <Form.Control.Feedback type="invalid">{t("general.required")}</Form.Control.Feedback>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3" controlId="country">
                <Form.Label column sm="4">
                  {t("congprofile.country")} <span className="text-danger">*</span>
                </Form.Label>
                <Col sm="8">
                  <Form.Select required value={countryId} onChange={(e) => setCountryId(Number(e.target.value))}>
                    {localizedCountryNames(i18n.language).map((ctry) => (
                      <option key={ctry.id} value={ctry.id}>
                        {ctry.localized}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3" controlId="timezone">
                <Form.Label column sm="4">
                  {t("congprofile.time-zone")} <span className="text-danger">*</span>
                </Form.Label>
                <Col sm="8">
                  <Form.Select required value={timezoneId} onChange={(e) => setTimezoneId(Number(e.target.value))}>
                    {HourglassGlobals.timezones().map((tz) => (
                      <option key={tz.id} value={tz.id}>
                        {`${tz.name} (${tz.offset})`}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Card.Body>
            <Card.Footer>
              <Row>
                <Col>
                  <Button variant="danger" onClick={handleBack}>
                    {t("tour.button.back")}
                  </Button>
                </Col>
                <Col>
                  <Button className="float-end" type="submit">
                    {t("tour.button.next")}
                  </Button>
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        </Form>
      );

    case Step.Finish:
      return (
        <Card className="mx-auto" style={{ maxWidth: "40rem" }}>
          <Card.Header>{t("smpw.registration")}</Card.Header>
          <Card.Body>
            <Table borderless>
              <tbody>
                <tr>
                  <td className="fw-bolder">{t("smpw.city-name")}</td>
                  <td className="fw-lighter">{name}</td>
                </tr>
                <tr>
                  <td className="fw-bolder">{t("congprofile.country")}</td>
                  <td className="fw-lighter">
                    {localizedCountryNames(i18n.language).find((c) => c.id === countryId)?.localized ?? "unknown"}
                  </td>
                </tr>
                <tr>
                  <td className="fw-bolder">{t("congprofile.time-zone")}</td>
                  <td className="fw-lighter">
                    {HourglassGlobals.timezones().find((t) => t.id === timezoneId)?.name ?? "unknown"}
                  </td>
                </tr>
              </tbody>
            </Table>
            <Card.Text className="mt-4">{t("smpw.registration-finish-text")}</Card.Text>
          </Card.Body>
          <Card.Footer>
            <Row>
              <Col>
                <Button variant="danger" onClick={handleBack}>
                  {t("tour.button.back")}
                </Button>
              </Col>
              <Col className="d-flex justify-content-end">
                <ShowStatus status={status} />
                <Button className="float-end ms-3" variant="success" onClick={handleSubmit}>
                  {t("general.submit")}
                </Button>
              </Col>
            </Row>
          </Card.Footer>
        </Card>
      );

    default:
      return <></>;
  }
}

// If user needs an account...
export function SMPWUserRegistration() {
  const [step, setStep] = useState(1);
  const [regState, setRegState] = useState<RegistrationState>({
    user: {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      googleLoggedIn: false,
      googleAuth: true,
      appleAuth: false,
      appleLoggedIn: false,
      requireApple: false,
      gRecaptchaSubmitted: false,
      gRecaptchaSuccess: false,
      weakPassword: false,
      invalidEmail: false,
      requireGoogle: false,
    },
    cong: {
      shareText: "",
      shareTextInvalid: false,
      approved: false,
      countriesId: 1,
      timezonesId: 36,
      localesId: 1,
      congNumInvalid: false,
      number: 0,
      name: "",
    },
  });

  const [progressValue, setProgressValue] = useState(0);
  const [userFormValidated, setUserFormValidated] = useState(false);

  useEffect(() => {
    if (step === 3) {
      // Reset progress to 0 when step 3 is reached
      setProgressValue(0);
      let pass = false;

      // Animate from 0 to 100 over 3 seconds (3000ms)
      const duration = 3000;
      const startTime = Date.now();

      const animateProgress = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / duration) * 100, 100);

        setProgressValue(progress);

        if (progress < 100) {
          requestAnimationFrame(animateProgress);
        }

        if (progress == 100 && pass) {
          setStep(4);
        }
      };

      smpwAPI.regUser(getModelFromState(regState)).then(() => {
        pass = true;
      });

      requestAnimationFrame(animateProgress);
    }
  }, [step, regState]);

  const handleUserChange = (name: keyof RegisterUser, value: any) => {
    const newUser: RegisterUser = { ...regState.user, [name]: value };

    if (name === "email" && typeof value === "string") {
      // force them to use google signin if they have a gmail address, and apple sign in if they have an icloud address
      if (value.trim().toLowerCase().endsWith("@gmail.com")) newUser.requireGoogle = true;
      else if (isAppleICloudDomain(value)) newUser.requireApple = true;
    }

    setRegState({ ...regState, user: newUser });
  };

  switch (step) {
    case 1: {
      const handleNextStep = () => {
        setUserFormValidated(true);

        // Check if user has completed authentication
        const isAuthenticated = true;
        // regState.user.googleLoggedIn || regState.user.appleLoggedIn || regState.user.gRecaptchaSuccess;

        if (isAuthenticated) {
          setStep?.(2);
        }
      };

      return (
        <>
          <UserInfo user={regState.user} formValidated={userFormValidated} onUserChange={handleUserChange} />
          <Button onClick={handleNextStep}>{t("tour.button.next")}</Button>
        </>
      );
    }

    case 2:
      return (
        <AddCongModal
          show={true}
          setShow={() => {}}
          langGroupId={0}
          regState={regState}
          setRegState={setRegState}
          smpwOnNext={() => setStep(3)}
          setShowManaged={() => setStep(-2)}
          noModal
          smpw
        />
      );

    case -2:
      return (
        <Alert variant="danger" className="m-5">
          {t("smpw.cong_registered_get_invite")}
        </Alert>
      );

    case 3:
      return (
        <Card className="p-3">
          <ProgressBar now={progressValue} />
        </Card>
      );

    case 4:
      redirectToZoneRegistrationSteps();
  }
}

export function redirectToZoneRegistrationSteps() {
  // setting the location.href is unlikely to take the browser to the page
  // as the user's session will reset; but it needs to be directed somewhere
  // and this is where we want it to go. To ensure that the browser gets there
  // eventually we'll set an item in local storage that can be intercepted
  // upon the reset and send the browser where it should go.
  setSMPWRegMode();
  window.location.href = "/v2/page/app/smpwreg";
}

export function setSMPWRegMode() {
  localStorage.setItem("smpwreg", "true");
}
